
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  GraduationCap,
  ShieldCheck,
  Users,
  Check,
} from "lucide-react";

const LOGO_URL =
  "https://res.cloudinary.com/hsvfv0h8/image/upload/v1786152037/Untitled_design__6_-removebg-preview_zijzg4.png";

const HERO_VECTOR_URL = "erp.svg";

export default function Landing() {
  const navigate = useNavigate();

  const portals = [
    
    {
      title: "Staff Portal",
      description: "Staff and institutional workspace",
      icon: Users,
      path: "/employee-login",
    },
    {
      title: "Student Portal",
      description: "Academic and student services",
      icon: GraduationCap,
      path: "/student-login",
    },
    {
      title: "Administrator",
      description: "Administration and system management",
      icon: ShieldCheck,
      path: "/admin-login",
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 antialiased">
      <div className="flex min-h-screen flex-col lg:flex-row">
        {/* =========================================================
            LEFT VISUAL SECTION
            ========================================================= */}
        <section
          className="
            relative
            hidden
            min-h-screen
            overflow-hidden
            bg-neutral-50
            lg:flex
            lg:w-1/2
            xl:w-[52%]
          "
        >
          {/* Logo - Desktop */}
          <div
            className="
              absolute
              left-6
              top-6
              z-20
              xl:left-10
              xl:top-8
            "
          >
            <img
              src={LOGO_URL}
              alt="Company logo"
              className="
                block
                h-auto
                w-auto
                max-h-[64px]
                max-w-[120px]
                object-contain
              "
              loading="eager"
              decoding="async"
            />
          </div>

          {/* Educational Vector */}
          <div
            className="
              absolute
              left-1/2
              top-1/2
              w-[72%]
              max-w-[620px]
              -translate-x-1/2
              -translate-y-1/2
              xl:w-[76%]
            "
          >
            <img
              src={HERO_VECTOR_URL}
              alt="Learning management system illustration"
              className="
                block
                h-auto
                w-full
                object-contain
              "
              loading="eager"
              decoding="async"
            />
          </div>

          {/* Subtle Background Shapes */}
          <div
            className="
              pointer-events-none
              absolute
              -bottom-32
              -left-32
              h-[420px]
              w-[420px]
              rounded-full
              bg-primary-500/[0.025]
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              -right-[180px]
              -top-40
              h-[500px]
              w-[500px]
              rounded-full
              bg-primary-500/[0.02]
            "
          />
        </section>

        {/* =========================================================
            RIGHT PORTAL SECTION
            ========================================================= */}
        <main
          className="
            relative
            flex
            min-h-screen
            flex-1
            items-center
            justify-center
            px-5
            py-24
            sm:px-8
            sm:py-28
            md:px-12
            lg:px-10
            lg:py-10
            xl:px-16
          "
        >
          {/* Logo - Mobile */}
          <div
            className="
              absolute
              left-5
              top-5
              z-20
              sm:left-8
              sm:top-7
              lg:hidden
            "
          >
            <img
              src={LOGO_URL}
              alt="Company logo"
              className="
                block
                h-auto
                w-auto
                max-h-[56px]
                max-w-[145px]
                object-contain
              "
              loading="eager"
              decoding="async"
            />
          </div>

          <div className="w-full max-w-md sm:max-w-lg lg:max-w-md xl:max-w-lg">
            {/* =====================================================
                PORTAL NAVIGATION
                ===================================================== */}
            <nav
              aria-label="Portal selection"
              className="border-y border-neutral-200"
            >
              {portals.map((portal) => {
                const Icon = portal.icon;

                return (
                  <button
                    key={portal.path}
                    type="button"
                    onClick={() => navigate(portal.path)}
                    className="
                      portal-button
                      group
                      flex
                      w-full
                      items-center
                      gap-3
                      border-b
                      border-neutral-200
                      bg-transparent
                      py-5
                      text-left
                      last:border-b-0
                      outline-none
                      ring-0
                      transition-transform
                      duration-200
                      ease-out

                      /* Hover movement only */
                      hover:translate-x-1
                      hover:bg-transparent
                      hover:outline-none
                      hover:ring-0

                      /* Remove click/focus rectangle */
                      focus:border-neutral-200
                      focus:bg-transparent
                      focus:outline-none
                      focus:ring-0

                      focus-visible:border-neutral-200
                      focus-visible:bg-transparent
                      focus-visible:outline-none
                      focus-visible:ring-0

                      /* Remove active background/border changes */
                      active:border-neutral-200
                      active:bg-transparent
                      active:outline-none
                      active:ring-0

                      sm:gap-4
                      sm:py-6
                    "
                  >
                    {/* =================================================
                        ICON
                        ================================================= */}
                    <span
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        bg-neutral-100
                        text-neutral-600
                        transition-all
                        duration-200
                        group-hover:bg-primary-50
                        group-hover:text-primary-700
                        sm:h-11
                        sm:w-11
                      "
                    >
                      <Icon
                        className="
                          h-[18px]
                          w-[18px]
                          transition-transform
                          duration-200
                          group-hover:scale-105
                          sm:h-5
                          sm:w-5
                        "
                      />
                    </span>

                    {/* =================================================
                        PORTAL INFORMATION
                        ================================================= */}
                    <span className="min-w-0 flex-1">
                      <span
                        className="
                          block
                          text-sm
                          font-semibold
                          text-neutral-900
                          transition-colors
                          duration-200
                          group-hover:text-primary-700
                          sm:text-[15px]
                        "
                      >
                        {portal.title}
                      </span>

                      <span
                        className="
                          mt-1
                          block
                          text-xs
                          leading-relaxed
                          text-neutral-500
                          transition-colors
                          duration-200
                          group-hover:text-neutral-600
                          sm:text-sm
                        "
                      >
                        {portal.description}
                      </span>
                    </span>

                    {/* =================================================
                        ARROW
                        ================================================= */}
                    <span
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-neutral-100
                        text-neutral-500
                        transition-all
                        duration-200
                        ease-out

                        /* Light background on hover */
                        group-hover:bg-primary-50

                        /* Primary/dark blue */
                        group-hover:text-primary-700

                        /* Scale */
                        group-hover:scale-110

                        sm:h-9
                        sm:w-9
                      "
                    >
                      <ArrowUpRight
                        className="
                          h-[17px]
                          w-[17px]
                          transition-transform
                          duration-200
                          group-hover:-translate-y-0.5
                          group-hover:translate-x-0.5
                        "
                      />
                    </span>
                  </button>
                );
              })}
            </nav>

            {/* =====================================================
                FOOTER
                ===================================================== */}
            <div
              className="
                mt-6
                flex
                items-center
                justify-between
                text-[10px]
                text-neutral-400
                sm:mt-7
                sm:text-xs
              "
            >
              {/* Secure Access */}
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="
                    flex
                    h-4
                    w-4
                    items-center
                    justify-center
                    rounded-full
                    bg-emerald-50
                    text-emerald-600
                  "
                >
                 
                </span>

                <span className="font-medium text-neutral-500">
               
                </span>
              </span>

              {/* Copyright */}
              <span className="text-neutral-400">
                © 2026 TFC elevate
              </span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

